import { Head, Link, router } from "@inertiajs/react"
import { Search } from "lucide-react"
import { useState } from "react"

import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

interface SearchResult {
  id: number
  id2?: number
  type: "Customer" | "Customer Contact" | "Case" | "Case Subject"
  description: string
  link_url: string
}

interface Filters {
  name: string
  phone: string
  email: string
  street: string
  subdivision: string
  search_type: string
}

interface Permissions {
  view_customers: boolean
  search_all_cases: boolean
}

interface Props {
  results: SearchResult[]
  filters: Filters
  permissions: Permissions
}

const breadcrumbs: BreadcrumbItem[] = [{ title: "Search", href: "/search" }]

const TYPE_COLORS: Record<string, string> = {
  Customer: "bg-blue-100 text-blue-800",
  "Customer Contact": "bg-green-100 text-green-800",
  Case: "bg-amber-100 text-amber-800",
  "Case Subject": "bg-purple-100 text-purple-800",
}

export default function SearchIndex({ results, filters }: Props) {
  const [name, setName] = useState(filters.name)
  const [phone, setPhone] = useState(filters.phone)
  const [email, setEmail] = useState(filters.email)
  const [street, setStreet] = useState(filters.street)
  const [subdivision, setSubdivision] = useState(filters.subdivision)
  const [searchType, setSearchType] = useState(filters.search_type)

  const hasSearched =
    filters.name !== "" ||
    filters.phone !== "" ||
    filters.email !== "" ||
    filters.street !== "" ||
    filters.subdivision !== ""

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    router.get(
      "/search",
      {
        name: name || undefined,
        phone: phone || undefined,
        email: email || undefined,
        street: street || undefined,
        subdivision: subdivision || undefined,
        search_type: searchType,
      },
      { preserveState: true },
    )
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Search" />

      <div className="mx-auto max-w-5xl px-4 py-6">
        <h1 className="text-hpi-dark-gray mb-4 text-xl font-semibold">
          Person Search
        </h1>

        {/* Search Form */}
        <form
          onSubmit={handleSearch}
          className="border-hpi-light-gray mb-6 rounded-lg border bg-white p-4"
        >
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
            <div>
              <label className="text-hpi-dark-gray mb-1 block text-xs font-medium">
                Name
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="border-hpi-light-gray text-hpi-dark-gray h-8 w-full rounded border px-2 text-sm focus:border-blue-400 focus:ring-1 focus:ring-blue-400 focus:outline-none"
              />
            </div>
            <div>
              <label className="text-hpi-dark-gray mb-1 block text-xs font-medium">
                Phone
              </label>
              <input
                type="text"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="border-hpi-light-gray text-hpi-dark-gray h-8 w-full rounded border px-2 text-sm focus:border-blue-400 focus:ring-1 focus:ring-blue-400 focus:outline-none"
              />
            </div>
            <div>
              <label className="text-hpi-dark-gray mb-1 block text-xs font-medium">
                Email
              </label>
              <input
                type="text"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="border-hpi-light-gray text-hpi-dark-gray h-8 w-full rounded border px-2 text-sm focus:border-blue-400 focus:ring-1 focus:ring-blue-400 focus:outline-none"
              />
            </div>
            <div>
              <label className="text-hpi-dark-gray mb-1 block text-xs font-medium">
                Street Name
              </label>
              <input
                type="text"
                value={street}
                onChange={(e) => setStreet(e.target.value)}
                className="border-hpi-light-gray text-hpi-dark-gray h-8 w-full rounded border px-2 text-sm focus:border-blue-400 focus:ring-1 focus:ring-blue-400 focus:outline-none"
              />
            </div>
            <div>
              <label className="text-hpi-dark-gray mb-1 block text-xs font-medium">
                Subdivision
              </label>
              <input
                type="text"
                value={subdivision}
                onChange={(e) => setSubdivision(e.target.value)}
                className="border-hpi-light-gray text-hpi-dark-gray h-8 w-full rounded border px-2 text-sm focus:border-blue-400 focus:ring-1 focus:ring-blue-400 focus:outline-none"
              />
            </div>
            <div>
              <label className="text-hpi-dark-gray mb-1 block text-xs font-medium">
                Type
              </label>
              <select
                value={searchType}
                onChange={(e) => setSearchType(e.target.value)}
                className="border-hpi-light-gray text-hpi-dark-gray h-8 w-full rounded border px-2 text-sm focus:border-blue-400 focus:ring-1 focus:ring-blue-400 focus:outline-none"
              >
                <option value="Wide">Wide</option>
                <option value="Exact">Exact</option>
              </select>
            </div>
          </div>
          <div className="mt-4 flex items-center gap-2">
            <button
              type="submit"
              className="bg-hpi-blue flex items-center gap-1.5 rounded px-4 py-1.5 text-sm font-medium text-white hover:bg-blue-700"
            >
              <Search className="h-3.5 w-3.5" />
              Search
            </button>
          </div>
        </form>

        {/* Results */}
        {hasSearched && (
          <div>
            <h2 className="text-hpi-dark-gray mb-3 text-lg font-semibold">
              Search Results
              {results.length > 0 && (
                <span className="text-hpi-med-gray ml-2 text-sm font-normal">
                  ({results.length} found)
                </span>
              )}
            </h2>

            {results.length === 0 ? (
              <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                No results found
              </div>
            ) : (
              <div className="border-hpi-light-gray overflow-hidden rounded-lg border bg-white">
                {results.map((result, index) => (
                  <div
                    key={`${result.type}-${result.id}-${result.id2 ?? 0}`}
                    className={`flex items-center gap-3 px-4 py-2.5 ${
                      index !== results.length - 1
                        ? "border-hpi-pale-gray border-b"
                        : ""
                    } hover:bg-gray-50`}
                  >
                    <span
                      className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${TYPE_COLORS[result.type] ?? "bg-gray-100 text-gray-800"}`}
                    >
                      {result.type}
                    </span>
                    <Link
                      href={result.link_url}
                      className="text-hpi-dark-gray hover:text-hpi-active-blue text-sm font-medium"
                    >
                      {result.description}
                    </Link>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {!hasSearched && (
          <div className="text-hpi-med-gray py-8 text-center text-sm">
            Enter search criteria above to find people and records.
          </div>
        )}
      </div>
    </AppLayout>
  )
}
